import { Form, Formik, FormikProps } from 'formik';
import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import { FormField } from 'src/components/shared/Forms/FormHelpers';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { ModalFooter } from 'src/components/shared/Modal/Modal';
import { selectInstrumentGroups } from 'src/store/instrumentgroups/instrumentgroups.selectors';
import { selectActiveInstruments } from 'src/store/instruments/instruments.selectors';
import { MethodDetailDto } from 'src/store/methods/method';
import {
  maxLength,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';

type Props = {
  method: Nullable<MethodDetailDto>;
  onCancel: () => void;
  onSubmit: (method: MethodDetailDto) => void;
};

export const MethodForm = forwardRef<FormikProps<MethodDetailDto>, Props>(
  (props, ref) => {
    const { onSubmit, onCancel } = props;

    const [showValidationErrors, setShowValidationErrors] =
      useState<boolean>(false);
    const [method] = useState<MethodDetailDto>(
      props.method || ({} as MethodDetailDto)
    );
    const { t } = useTranslation();

    const instrumentGroups = useSelector(selectInstrumentGroups);
    const instruments = useSelector(selectActiveInstruments);

    const validate = (method: MethodDetailDto) => {
      const validationRunners = [
        validationRunner('name', t('props.name'), required, maxLength(50)),
        validationRunner('description', t('props.description'), maxLength(200)),
        validationRunner('unit', t('props.unit'), maxLength(10)),
        validationRunner(
          'instrumentGroupId',
          t('instrumentGroup.singular'),
          required
        ),
        validationRunner('instrumentId', t('instrument.singular'), required)
      ];
      return runValidations(method, validationRunners);
    };

    return (
      <Formik
        initialValues={method}
        validate={validate}
        innerRef={ref}
        onSubmit={(values, { setSubmitting }) => {
          onSubmit(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          submitCount
        }) => (
          <Form onSubmit={handleSubmit}>
            <InputField
              name="name"
              label={t('props.name')}
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.name}
              displayError={touched.name || submitCount > 0}
            />
            <InputField
              name="nameNO"
              label={t('language.norwegian')}
              value={values.nameNO}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.nameNO}
              displayError={touched.nameNO || submitCount > 0}
            />
            <InputField
              name="nameEN"
              label={t('language.english')}
              value={values.nameEN}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.nameEN}
              displayError={touched.nameEN || submitCount > 0}
            />
            <InputField
              name="nameDE"
              label={t('language.german')}
              value={values.nameDE}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.nameDE}
              displayError={touched.nameDE || submitCount > 0}
            />
            <InputField
              name="description"
              label={t('props.description')}
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.description}
              displayError={touched.description || submitCount > 0}
            />
            <InputField
              name="unit"
              label={t('props.unit')}
              value={values.unit}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.unit}
              displayError={touched.unit || submitCount > 0}
            />
            <FormField>
              <Autocomplete
                options={instrumentGroups}
                label={t('instrumentGroup.singular')}
                optionField="name"
                value={values.instrumentGroup}
                onChange={instrumentGroup =>
                  setFieldValue('instrumentGroupId', instrumentGroup?.id)
                }
                onBlur={handleBlur}
                errorMessage={errors.instrumentGroupId}
                displayError={
                  !!errors.instrumentGroupId && showValidationErrors
                }
              />
            </FormField>
            <FormField>
              <Autocomplete
                options={instruments}
                label={t('method.instrument')}
                optionField="name"
                value={values.instrument}
                onChange={instrument =>
                  setFieldValue('instrumentId', instrument?.id)
                }
                onBlur={handleBlur}
                errorMessage={errors.instrumentId}
                displayError={!!errors.instrumentId && showValidationErrors}
              />
            </FormField>

            <ModalFooter>
              <TertiaryButton
                onClick={onCancel}
                label={t('cancel')}
                className="mr-32"
              />
              <PrimaryButton
                type="submit"
                label={t('save.verb')}
                disabled={isSubmitting}
                onClick={() => {
                  setShowValidationErrors(true);
                }}
              />
            </ModalFooter>
          </Form>
        )}
      </Formik>
    );
  }
);
